// src/hooks/consult/useCaptureTelemetry.ts

import { useEffect, useRef } from "react";

import type { CaptureStats } from "../../services/audioStreamingService";
import type { TelemetrySocket } from "./useConsultMediaTelemetry";

// Deliberately a timer, not the audio clock: a frame-driven sample would go silent exactly when frames stop.
export const CAPTURE_TELEMETRY_INTERVAL_MS = 60_000;

export interface CaptureTelemetrySource {
	getCaptureStats(): CaptureStats;
}

export interface UseCaptureTelemetryOptions {
	enabled: boolean;
	roomId: string;
	getSocket: () => TelemetrySocket | null | undefined;
	getCapture: () => CaptureTelemetrySource | null | undefined;
	isDisabled?: () => boolean;
}

export interface AudioCapturePayload {
	roomId: string;
	v: 1;
	kind: "audio_capture";
	state: CaptureStats["state"] | "absent" | "disabled";
	windowMs: number;
	hiddenMs: number;
	frames: number;
	voiceMs: number;
	packets: number;
	queued: number;
	bytes: number;
}

const ZERO_STATS: CaptureStats = { state: "closed", frames: 0, voiceMs: 0, packets: 0, queued: 0, bytes: 0 };

const delta = (current: number, previous: number) => Math.max(0, Math.round(current - previous));

export function useCaptureTelemetry(options: UseCaptureTelemetryOptions): void {
	const optionsRef = useRef(options);
	optionsRef.current = options;
	const warnedRef = useRef(false);

	useEffect(() => {
		if (!options.enabled) return;

		const warnOnce = (error: unknown) => {
			if (warnedRef.current) return;
			warnedRef.current = true;
			console.warn("consult.telemetry.capture_failed", { errorName: (error as { name?: string } | null)?.name ?? "unknown" });
		};

		let windowStart = Date.now();
		let hiddenMs = 0;
		let hiddenSince: number | null = document.visibilityState === "hidden" ? windowStart : null;
		// Keyed on the capture object, so a rebuilt service starts from zero rather than from its predecessor's totals.
		let baseline: { capture: CaptureTelemetrySource; stats: CaptureStats } | null = null;

		const onVisibilityChange = () => {
			const now = Date.now();
			if (document.visibilityState === "hidden") {
				if (hiddenSince === null) hiddenSince = now;
			} else if (hiddenSince !== null) {
				hiddenMs += now - hiddenSince;
				hiddenSince = null;
			}
		};

		const readCapture = () => {
			const capture = optionsRef.current.getCapture();
			if (!capture) return null;
			try {
				return { capture, stats: capture.getCaptureStats() };
			} catch (error) {
				warnOnce(error);
				return null;
			}
		};

		const sample = () => {
			const now = Date.now();
			const windowMs = now - windowStart;
			if (hiddenSince !== null) {
				hiddenMs += now - hiddenSince;
				hiddenSince = now;
			}
			const windowHiddenMs = hiddenMs;
			windowStart = now;
			hiddenMs = 0;

			const current = readCapture();
			const previous = current && baseline?.capture === current.capture ? baseline.stats : ZERO_STATS;
			baseline = current;
			const stats = current?.stats ?? ZERO_STATS;

			const { roomId, getSocket, isDisabled } = optionsRef.current;
			// A disabled transcriber that still holds a service reports that service's counters rather than hiding them.
			const state: AudioCapturePayload["state"] = isDisabled?.() ? "disabled" : current ? stats.state : "absent";
			const socket = getSocket();
			if (!socket?.connected) return;

			const payload: AudioCapturePayload = {
				roomId,
				v: 1,
				kind: "audio_capture",
				state,
				windowMs: Math.max(0, windowMs),
				hiddenMs: Math.max(0, windowHiddenMs),
				frames: delta(stats.frames, previous.frames),
				voiceMs: delta(stats.voiceMs, previous.voiceMs),
				packets: delta(stats.packets, previous.packets),
				queued: delta(stats.queued, previous.queued),
				bytes: delta(stats.bytes, previous.bytes),
			};
			socket.emit("consultTelemetry", payload);
		};

		document.addEventListener("visibilitychange", onVisibilityChange);
		const timer = setInterval(() => {
			try {
				sample();
			} catch (error) {
				warnOnce(error);
			}
		}, CAPTURE_TELEMETRY_INTERVAL_MS);
		return () => {
			clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [options.enabled]);
}
